// ==============================================
// Matches Page
// ==============================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { matchesAPI, metadataAPI } from '../services/api';
import { Match, MatchFormData, MatchSet, OpenMatchRequest, PlayerLevel, User } from '../types';
import { useAuth } from '../contexts/AuthContext';
import Modal from '../components/Modal';
import PlayerSearchPicker from '../components/PlayerSearchPicker';
import { playerPath } from '../utils/slug';

const SITE_URL = process.env.REACT_APP_SITE_URL || '';

const pad = (n: number) => String(n).padStart(2, '0');

function localIso(d: Date = new Date()) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fmtDate(iso: string): string {
  const [y, mo, day] = iso.split('-').map(Number);
  return new Date(y, mo - 1, day).toLocaleDateString('ro-RO', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
}

function fmtTime(t?: string | null) {
  return t ? t.substring(0, 5) : 'Fără oră';
}

function fmtDateTime(value?: string | null) {
  if (!value) return null;
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('ro-RO', { day: 'numeric', month: 'short' }) + ', ' +
    d.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
}

function hoursUntil(value?: string | null) {
  if (!value) return null;
  const target = new Date(value.replace(' ', 'T')).getTime();
  if (Number.isNaN(target)) return null;
  const hours = Math.ceil((target - Date.now()) / 3600000);
  if (hours <= 0) return 'în curând';
  if (hours === 1) return '1 oră';
  return `${hours} ore`;
}

function matchTypeLabel(slug?: string | null) {
  if (slug === 'competitive') return 'Competitiv';
  if (slug === 'tournament') return 'Turneu';
  return 'Amical';
}

function matchTypeColor(slug?: string | null) {
  if (slug === 'competitive') return { bg: '#fef3e8', color: '#c2410c', border: '#fed7aa' };
  if (slug === 'tournament') return { bg: '#e8e9f5', color: '#21257B', border: '#c7d2fe' };
  return { bg: '#ecfdf5', color: '#047857', border: '#bbf7d0' };
}

function resultFor(match: Match, currentUserId?: number | null): 'win' | 'loss' | 'tie' | 'pending' {
  if (match.status === 'scheduled' || match.status === 'pending_confirmation' || !match.result) return 'pending';
  if (match.result === 'tie') return 'tie';
  return match.winner_id === currentUserId ? 'win' : 'loss';
}

function opponentName(match: Match, currentUserId?: number | null) {
  const isPlayer1 = match.player1_id === currentUserId;
  return isPlayer1
    ? `${match.player2_first_name ?? ''} ${match.player2_last_name ?? ''}`.trim()
    : `${match.player1_first_name ?? ''} ${match.player1_last_name ?? ''}`.trim();
}

function playerName(match: Match, side: 'player1' | 'player2') {
  return side === 'player1'
    ? `${match.player1_first_name ?? ''} ${match.player1_last_name ?? ''}`.trim()
    : `${match.player2_first_name ?? ''} ${match.player2_last_name ?? ''}`.trim();
}

function playerAvatar(match: Match, side: 'player1' | 'player2') {
  return side === 'player1'
    ? (match as any).player1_avatar_url ?? null
    : (match as any).player2_avatar_url ?? null;
}

function setSummary(sets: MatchSet[] = []) {
  return sets.map(s => `${s.player1_score}-${s.player2_score}`).join(', ');
}

function scorePreview(sets: MatchSet[]) {
  let p1 = 0;
  let p2 = 0;
  sets.forEach(s => {
    if (Number(s.player1_score) > Number(s.player2_score)) p1++;
    else if (Number(s.player2_score) > Number(s.player1_score)) p2++;
  });
  return { p1, p2, result: p1 > p2 ? 'player1' : p2 > p1 ? 'player2' : 'tie' };
}

const emptySets = (): MatchSet[] => [
  { set_number: 1, player1_score: 11, player2_score: 0 },
  { set_number: 2, player1_score: 11, player2_score: 0 },
  { set_number: 3, player1_score: 11, player2_score: 0 },
];

const TypeChip: React.FC<{ slug?: string | null }> = ({ slug }) => {
  const c = matchTypeColor(slug);
  return (
    <span style={{
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      borderRadius: 999, padding: '.13rem .55rem', fontSize: '.72rem', fontWeight: 700,
    }}>
      {matchTypeLabel(slug)}
    </span>
  );
};

const LevelChip: React.FC<{ name?: string | null; muted?: boolean }> = ({ name, muted = false }) => {
  if (!name) return null;
  return (
    <span style={{
      border: '1px solid var(--tt-border)',
      background: muted ? 'var(--tt-surface-2)' : '#eff6ff',
      color: muted ? 'var(--tt-text-muted)' : 'var(--tt-primary)',
      borderRadius: 999,
      padding: '.13rem .55rem',
      fontSize: '.72rem',
      fontWeight: 800,
      whiteSpace: 'nowrap',
    }}>
      {name}
    </span>
  );
};

const ResultBadge: React.FC<{ result: ReturnType<typeof resultFor> }> = ({ result }) => {
  const cfg = {
    win:     { label: 'W', bg: '#10b981' },
    loss:    { label: 'L', bg: '#ef4444' },
    tie:     { label: 'T', bg: '#64748b' },
    pending: { label: '?', bg: '#94a3b8' },
  }[result];
  return (
    <div style={{
      width: 34, height: 34, borderRadius: 9, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: cfg.bg, color: '#fff', fontWeight: 800, fontSize: '.85rem',
    }}>
      {cfg.label}
    </div>
  );
};

const AvatarBubble: React.FC<{ name: string; src?: string | null; size?: number }> = ({ name, src, size = 38 }) => {
  const initials = name.split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();
  const url = src ? `${SITE_URL}${src}` : null;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: url ? 'transparent' : 'linear-gradient(135deg, var(--tt-primary), var(--tt-accent))',
      color: '#fff', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 800, fontSize: size * 0.34, border: '2px solid var(--tt-surface)',
      boxShadow: '0 2px 8px rgba(15,23,42,.12)',
    }}>
      {url ? <img src={url} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials}
    </div>
  );
};

const MiniPlayer: React.FC<{ name: string; avatar?: string | null; align?: 'left' | 'right'; to?: string }> = ({ name, avatar, align = 'left', to }) => {
  const content = (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '.55rem',
      flexDirection: align === 'right' ? 'row-reverse' : 'row',
      minWidth: 0,
    }}>
      <AvatarBubble name={name || 'Jucător'} src={avatar} />
      <div style={{ minWidth: 0, textAlign: align === 'right' ? 'right' : 'left' }}>
        <div style={{ fontWeight: 800, fontSize: '.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {name || 'Jucător'}
        </div>
      </div>
    </div>
  );

  if (!to) return content;

  return (
    <Link
      to={to}
      className="mini-player-link"
      style={{
        color: 'inherit',
        textDecoration: 'none',
        minWidth: 0,
        display: 'block',
      }}
    >
      {content}
    </Link>
  );
};

const SetEditor: React.FC<{
  sets: MatchSet[];
  onChange: (sets: MatchSet[]) => void;
}> = ({ sets, onChange }) => {
  const updateSet = (idx: number, field: 'player1_score' | 'player2_score', value: string) => {
    onChange(sets.map((s, i) => i === idx ? { ...s, [field]: Number(value) } : s));
  };

  return (
    <div style={{ display: 'grid', gap: '.65rem' }}>
      {sets.map((s, idx) => (
        <div key={idx} style={{
          display: 'grid', gridTemplateColumns: '58px minmax(0, 1fr) 24px minmax(0, 1fr) 38px',
          gap: '.5rem', alignItems: 'center',
          background: 'var(--tt-surface-2)', border: '1px solid var(--tt-border)',
          borderRadius: 12, padding: '.5rem',
        }}>
          <div style={{ fontSize: '.76rem', color: 'var(--tt-text-muted)', fontWeight: 800, textAlign: 'center' }}>Set {idx + 1}</div>
          <input className="form-control text-center" type="number" min={0} value={s.player1_score} onChange={e => updateSet(idx, 'player1_score', e.target.value)} style={{ borderRadius: 10, fontWeight: 800 }} />
          <span style={{ textAlign: 'center', color: 'var(--tt-text-muted)', fontWeight: 900 }}>–</span>
          <input className="form-control text-center" type="number" min={0} value={s.player2_score} onChange={e => updateSet(idx, 'player2_score', e.target.value)} style={{ borderRadius: 10, fontWeight: 800 }} />
          <button
            type="button"
            disabled={sets.length <= 1}
            onClick={() => onChange(sets.filter((_, i) => i !== idx))}
            aria-label="Șterge set"
            style={{
              width: 34, height: 34, minWidth: 34, borderRadius: '50%', padding: 0,
              border: '1px solid var(--tt-border)', background: 'var(--tt-surface)',
              color: 'var(--tt-text-muted)', cursor: sets.length <= 1 ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1rem', lineHeight: 1, boxShadow: 'none',
            }}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
};

const SetStrip: React.FC<{ sets?: MatchSet[]; player1Name: string; player2Name: string }> = ({ sets = [], player1Name, player2Name }) => {
  if (sets.length === 0) return null;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: '.35rem', flexWrap: 'wrap', marginTop: '.55rem',
    }}>
      {sets.map((s, idx) => {
        const p1Won = s.player1_score > s.player2_score;
        const p2Won = s.player2_score > s.player1_score;
        return (
          <div
            key={s.set_number ?? idx}
            title={`Set ${idx + 1}: ${player1Name} ${s.player1_score} - ${s.player2_score} ${player2Name}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '.25rem',
              border: '1px solid var(--tt-border)', borderRadius: 999,
              background: 'var(--tt-surface)', padding: '.15rem .45rem',
              fontSize: '.72rem', fontWeight: 800, color: 'var(--tt-text-2)',
              boxShadow: '0 1px 2px rgba(15,23,42,.04)',
            }}
          >
            <span style={{ color: p1Won ? '#059669' : 'var(--tt-text-muted)' }}>{s.player1_score}</span>
            <span style={{ color: 'var(--tt-text-muted)', fontWeight: 600 }}>-</span>
            <span style={{ color: p2Won ? '#059669' : 'var(--tt-text-muted)' }}>{s.player2_score}</span>
          </div>
        );
      })}
    </div>
  );
};

const AddMatchModal: React.FC<{
  currentUser: User;
  match?: Match;
  onClose: () => void;
  onCreated: () => void;
}> = ({ currentUser, match, onClose, onCreated }) => {
  const isEdit = !!match;
  const hasExistingScore = !!match && ((match.sets?.length ?? 0) > 0 || match.status === 'pending_confirmation');
  const initialOpponent = match ? [{
    id: match.player2_id,
    email: '',
    first_name: match.player2_first_name || '',
    last_name: match.player2_last_name || '',
    phone: null,
    avatar_url: (match as any).player2_avatar_url || null,
    status: 'active',
    email_verified: true,
    roles: [],
    created_at: '',
    updated_at: '',
  } as User] : [];

  const [opponent, setOpponent] = useState<User[]>(initialOpponent);
  const [date, setDate] = useState(match?.match_date || localIso());
  const [time, setTime] = useState(match?.match_time ? match.match_time.substring(0, 5) : '');
  const [type, setType] = useState<'friendly' | 'competitive'>(
    ((match?.match_type_slug || match?.match_type) === 'friendly') ? 'friendly' : 'competitive',
  );
  const [finished, setFinished] = useState(isEdit ? hasExistingScore : true);
  const [sets, setSets] = useState<MatchSet[]>(match?.sets && match.sets.length > 0 ? match.sets : emptySets);
  const [notes, setNotes] = useState(match?.notes || '');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cleanSets = sets.map((s, idx) => ({
    set_number: idx + 1,
    player1_score: Number(s.player1_score),
    player2_score: Number(s.player2_score),
  }));
  const preview = scorePreview(cleanSets);
  const scoreError = finished
    ? cleanSets.some(s => s.player1_score < 0 || s.player2_score < 0)
      ? 'Scorurile nu pot fi negative.'
      : cleanSets.some(s => s.player1_score === s.player2_score)
        ? 'Un set nu poate fi egal.'
        : null
    : null;

  const submit = async () => {
    if (!opponent[0]) { setError('Alege un adversar.'); return; }
    if (scoreError) { setError(scoreError); return; }

    try {
      setSubmitting(true);
      setError(null);
      const payload: MatchFormData = {
        player2_id: opponent[0].id,
        match_date: date,
        match_time: time || null,
        match_type_slug: type,
        notes: notes || null,
        sets: finished ? cleanSets : undefined,
      };
      const res = isEdit && match
        ? await matchesAPI.update(match.id, payload)
        : await matchesAPI.create(payload);
      if (res.success) {
        onCreated();
        onClose();
      } else {
        setError(res.message || 'Meciul nu a putut fi salvat.');
      }
    } catch (err: any) {
      setError(err.message || 'A apărut o eroare.');
    } finally {
      setSubmitting(false);
    }
  };

  const requestSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (finished) setConfirming(true);
    else submit();
  };

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        title={isEdit ? 'Editează meci' : 'Adaugă meci'}
        maxWidth={620}
        footer={
          <div style={{ display: 'flex', gap: '.75rem' }}>
            <button className="btn btn-outline-secondary" onClick={onClose} disabled={submitting} style={{ flex: 1, borderRadius: 10 }}>
              Anulează
            </button>
            <button className="btn btn-primary" type="submit" form="add-match-form" disabled={submitting} style={{ flex: 2, borderRadius: 10, fontWeight: 700 }}>
              {submitting ? <><span className="spinner-border spinner-border-sm me-2" />Se salvează…</> : (isEdit ? 'Salvează modificările' : 'Adaugă meci')}
            </button>
          </div>
        }
      >
        <form id="add-match-form" onSubmit={requestSubmit}>
          <div className="row g-3 mb-3">
            <div className="col-sm-6">
              <label className="form-label">Data</label>
              <input className="form-control" type="date" value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div className="col-sm-6">
              <label className="form-label">Ora</label>
              <input className="form-control" type="time" value={time} onChange={e => setTime(e.target.value)} />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Adversar</label>
            <PlayerSearchPicker
              selected={opponent}
              onAdd={u => setOpponent([u])}
              onRemove={() => setOpponent([])}
              excludeIds={[currentUser.id]}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Tip meci</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '.75rem' }}>
              {[
                { key: 'friendly' as const, title: 'Amical', desc: 'Apare în istoricul tău, dar nu influențează clasamentul.' },
                { key: 'competitive' as const, title: 'Competitiv', desc: 'Contează în clasament după confirmarea adversarului.' },
              ].map(opt => {
                const active = type === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setType(opt.key)}
                    style={{
                      textAlign: 'left', borderRadius: 10, padding: '.85rem 1rem',
                      border: `1.5px solid ${active ? 'var(--tt-primary)' : 'var(--tt-border)'}`,
                      background: active ? '#eff6ff' : 'var(--tt-surface)',
                      color: 'var(--tt-text)', cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontWeight: 800, marginBottom: '.2rem' }}>{opt.title}</div>
                    <div style={{ fontSize: '.78rem', color: 'var(--tt-text-muted)', lineHeight: 1.45 }}>{opt.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mb-3" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: '1rem', background: 'var(--tt-surface-2)', border: '1px solid var(--tt-border)',
            borderRadius: 10, padding: '.75rem 1rem',
          }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '.9rem' }}>Meci finalizat</div>
              <div style={{ color: 'var(--tt-text-muted)', fontSize: '.78rem' }}>
                {hasExistingScore ? 'Scorul există deja și poate fi modificat mai jos.' : 'Adaugă scorul acum sau păstrează meciul ca programat.'}
              </div>
            </div>
            <input
              type="checkbox"
              checked={finished}
              disabled={hasExistingScore}
              onChange={e => setFinished(e.target.checked)}
              style={{ width: 18, height: 18 }}
            />
          </div>

          {finished && (
            <div className="mb-3">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.6rem' }}>
                <label className="form-label mb-0">Scor pe seturi</label>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setSets(prev => [...prev, { set_number: prev.length + 1, player1_score: 11, player2_score: 0 }])}
                  style={{ borderRadius: 8 }}
                >
                  + Set
                </button>
              </div>
              <SetEditor sets={sets} onChange={setSets} />
              {scoreError && <div className="text-danger mt-2" style={{ fontSize: '.8rem' }}>{scoreError}</div>}
            </div>
          )}

          <div className="mb-1">
            <label className="form-label">Note</label>
            <textarea className="form-control" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>

          {error && <div className="alert alert-danger py-2 mt-3 mb-0" style={{ fontSize: '.85rem' }}>{error}</div>}
        </form>
      </Modal>

      <Modal
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        title="Confirmă scorul"
        maxWidth={480}
        footer={
          <div style={{ display: 'flex', gap: '.75rem' }}>
            <button className="btn btn-outline-secondary" onClick={() => setConfirming(false)} disabled={submitting} style={{ flex: 1, borderRadius: 10 }}>
              Înapoi
            </button>
            <button className="btn btn-primary" onClick={submit} disabled={submitting || !!scoreError} style={{ flex: 2, borderRadius: 10, fontWeight: 700 }}>
              Confirmă și salvează
            </button>
          </div>
        }
      >
        <div style={{ background: 'var(--tt-surface-2)', border: '1px solid var(--tt-border)', borderRadius: 12, padding: '1rem' }}>
          <div style={{ fontWeight: 800, marginBottom: '.5rem' }}>
            {currentUser.first_name} {currentUser.last_name} vs {opponent[0]?.first_name} {opponent[0]?.last_name}
          </div>
          <div style={{ fontSize: '1.8rem', fontWeight: 900, marginBottom: '.35rem' }}>
            {preview.p1} – {preview.p2}
          </div>
          <div style={{ color: 'var(--tt-text-muted)', fontSize: '.84rem' }}>{setSummary(cleanSets)}</div>
          <div style={{ marginTop: '.8rem', fontWeight: 700 }}>
            {preview.result === 'tie'
              ? 'Rezultat: egal'
              : `Câștigător: ${preview.result === 'player1' ? currentUser.first_name : opponent[0]?.first_name}`}
          </div>
          <div style={{ marginTop: '.6rem', color: 'var(--tt-text-muted)', fontSize: '.8rem', lineHeight: 1.5 }}>
            Adversarul va avea 24 de ore pentru confirmare. După aceea scorul se confirmă automat.
          </div>
        </div>
      </Modal>
    </>
  );
};

const ScoreMatchModal: React.FC<{
  match: Match;
  currentUser: User;
  onClose: () => void;
  onSaved: () => void;
}> = ({ match, currentUser, onClose, onSaved }) => {
  const [sets, setSets] = useState<MatchSet[]>(match.sets && match.sets.length > 0 ? match.sets : emptySets);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cleanSets = sets.map((s, idx) => ({
    set_number: idx + 1,
    player1_score: Number(s.player1_score),
    player2_score: Number(s.player2_score),
  }));
  const preview = scorePreview(cleanSets);
  const opponent = opponentName(match, currentUser.id);
  const opponentSide = match.player1_id === currentUser.id ? 'player2' : 'player1';
  const opponentProfilePath = opponentSide === 'player1'
    ? playerPath({ id: match.player1_id, first_name: match.player1_first_name || '', last_name: match.player1_last_name || '' })
    : playerPath({ id: match.player2_id, first_name: match.player2_first_name || '', last_name: match.player2_last_name || '' });
  const scoreError = cleanSets.some(s => s.player1_score < 0 || s.player2_score < 0)
    ? 'Scorurile nu pot fi negative.'
    : cleanSets.some(s => s.player1_score === s.player2_score)
      ? 'Un set nu poate fi egal.'
      : null;

  const submit = async () => {
    if (scoreError) { setError(scoreError); return; }
    try {
      setSubmitting(true);
      setError(null);
      const res = await matchesAPI.updateResult(match.id, { sets: cleanSets });
      if (res.success) {
        onSaved();
        onClose();
      } else {
        setError(res.message || 'Scorul nu a putut fi salvat.');
      }
    } catch (err: any) {
      setError(err.message || 'A apărut o eroare.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        title="Adaugă scor"
        maxWidth={560}
        footer={
          <div style={{ display: 'flex', gap: '.75rem' }}>
            <button className="btn btn-outline-secondary" onClick={onClose} disabled={submitting} style={{ flex: 1, borderRadius: 10 }}>
              Anulează
            </button>
            <button className="btn btn-primary" onClick={() => setConfirming(true)} disabled={submitting || !!scoreError} style={{ flex: 2, borderRadius: 10, fontWeight: 700 }}>
              Continuă
            </button>
          </div>
        }
      >
        <div style={{ marginBottom: '1rem' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto minmax(0,1fr)',
            alignItems: 'center', gap: '.75rem', marginBottom: '.9rem',
          }}>
            <MiniPlayer
              name={`${currentUser.first_name} ${currentUser.last_name}`}
              avatar={currentUser.avatar_url}
              to={playerPath(currentUser)}
            />
            <div style={{
              width: 34, height: 34, borderRadius: '50%', background: 'var(--tt-surface-2)',
              border: '1px solid var(--tt-border)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: '.7rem', fontWeight: 900, color: 'var(--tt-text-muted)',
            }}>VS</div>
            <MiniPlayer
              name={opponent}
              avatar={playerAvatar(match, opponentSide)}
              align="right"
              to={opponentProfilePath}
            />
          </div>
        </div>
        <SetEditor sets={sets} onChange={setSets} />
        <button
          type="button"
          className="btn btn-outline-primary btn-sm mt-3"
          onClick={() => setSets(prev => [...prev, { set_number: prev.length + 1, player1_score: 11, player2_score: 0 }])}
          style={{ borderRadius: 8 }}
        >
          + Set
        </button>
        {scoreError && <div className="text-danger mt-2" style={{ fontSize: '.8rem' }}>{scoreError}</div>}
        {error && <div className="alert alert-danger py-2 mt-3 mb-0" style={{ fontSize: '.85rem' }}>{error}</div>}
      </Modal>

      <Modal
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        title="Confirmă scorul"
        maxWidth={460}
        footer={
          <div style={{ display: 'flex', gap: '.75rem' }}>
            <button className="btn btn-outline-secondary" onClick={() => setConfirming(false)} disabled={submitting} style={{ flex: 1, borderRadius: 10 }}>
              Înapoi
            </button>
            <button className="btn btn-primary" onClick={submit} disabled={submitting || !!scoreError} style={{ flex: 2, borderRadius: 10, fontWeight: 700 }}>
              Confirmă scorul
            </button>
          </div>
        }
      >
        <div style={{ background: 'var(--tt-surface-2)', border: '1px solid var(--tt-border)', borderRadius: 12, padding: '1rem' }}>
          <div style={{ fontWeight: 800, marginBottom: '.5rem' }}>vs {opponent}</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 900 }}>{preview.p1} – {preview.p2}</div>
          <div style={{ color: 'var(--tt-text-muted)', fontSize: '.84rem' }}>{setSummary(cleanSets)}</div>
          <div style={{ marginTop: '.8rem', fontWeight: 700 }}>
            {preview.result === 'tie' ? 'Rezultat: egal' : `Câștigător: ${preview.result === 'player1' ? currentUser.first_name : opponent}`}
          </div>
          <div style={{ marginTop: '.6rem', color: 'var(--tt-text-muted)', fontSize: '.8rem', lineHeight: 1.5 }}>
            Adversarul va avea 24 de ore pentru confirmare.
          </div>
        </div>
      </Modal>
    </>
  );
};

const OpenMatchModal: React.FC<{
  currentUser: User;
  levels: PlayerLevel[];
  onClose: () => void;
  onCreated: () => void;
}> = ({ currentUser, levels, onClose, onCreated }) => {
  const [type, setType] = useState<'friendly' | 'competitive'>('competitive');
  const [allowedLevelIds, setAllowedLevelIds] = useState<number[]>(() => currentUser.level_id ? [currentUser.level_id] : []);
  const [flexible, setFlexible] = useState(true);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (allowedLevelIds.length === 0) {
      setError('Alege cel puțin un nivel acceptat.');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      const res = await matchesAPI.createOpenMatch({
        requested_match_type_slug: type,
        allowed_level_ids: allowedLevelIds,
        is_flexible_time: flexible,
        requested_start_datetime: flexible ? null : start || null,
        requested_end_datetime: flexible ? null : end || null,
        notes: notes || null,
      });
      if (res.success) {
        onCreated();
        onClose();
      } else {
        setError(res.message || 'Provocarea nu a putut fi postată.');
      }
    } catch (err: any) {
      setError(err.message || 'A apărut o eroare.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleLevel = (levelId: number) => {
    setAllowedLevelIds(prev =>
      prev.includes(levelId) ? prev.filter(id => id !== levelId) : [...prev, levelId],
    );
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Provocare deschisă"
      maxWidth={520}
      footer={
        <div style={{ display: 'flex', gap: '.75rem' }}>
          <button className="btn btn-outline-secondary" onClick={onClose} disabled={submitting} style={{ flex: 1, borderRadius: 10 }}>Anulează</button>
          <button className="btn btn-primary" type="submit" form="open-match-form" disabled={submitting} style={{ flex: 2, borderRadius: 10, fontWeight: 700 }}>
            Postează
          </button>
        </div>
      }
    >
      <form id="open-match-form" onSubmit={submit}>
        <div className="mb-3">
          <label className="form-label">Tip</label>
          <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
            {[
              ['competitive', 'Competitiv'],
              ['friendly', 'Amical'],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setType(key as 'friendly' | 'competitive')}
                style={{
                  border: `1.5px solid ${type === key ? 'var(--tt-primary)' : 'var(--tt-border)'}`,
                  background: type === key ? 'var(--tt-primary)' : 'var(--tt-surface)',
                  color: type === key ? '#fff' : 'var(--tt-text-2)',
                  borderRadius: 999, padding: '.38rem .9rem', fontSize: '.85rem',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="mb-3">
          <label className="form-label">Niveluri acceptate</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.45rem' }}>
            {levels.map(level => {
              const active = allowedLevelIds.includes(level.id);
              return (
                <button
                  key={level.id}
                  type="button"
                  onClick={() => toggleLevel(level.id)}
                  style={{
                    border: `1.5px solid ${active ? 'var(--tt-primary)' : 'var(--tt-border)'}`,
                    background: active ? '#eff6ff' : 'var(--tt-surface)',
                    color: active ? 'var(--tt-primary)' : 'var(--tt-text-2)',
                    borderRadius: 999,
                    padding: '.32rem .75rem',
                    fontSize: '.82rem',
                    fontWeight: active ? 800 : 500,
                  }}
                >
                  {level.display_name}
                </button>
              );
            })}
          </div>
          <div style={{ color: 'var(--tt-text-muted)', fontSize: '.76rem', marginTop: '.4rem' }}>
            Implicit este selectat nivelul tău. Poți adăuga mai multe niveluri compatibile.
          </div>
        </div>
        <div className="mb-3" style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
          <input type="checkbox" checked={flexible} onChange={e => setFlexible(e.target.checked)} />
          <span style={{ fontSize: '.875rem' }}>Orar flexibil</span>
        </div>
        {!flexible && (
          <div className="row g-2 mb-3">
            <div className="col-sm-6">
              <label className="form-label">Început</label>
              <input className="form-control" type="datetime-local" value={start} onChange={e => setStart(e.target.value)} />
            </div>
            <div className="col-sm-6">
              <label className="form-label">Sfârșit</label>
              <input className="form-control" type="datetime-local" value={end} onChange={e => setEnd(e.target.value)} />
            </div>
          </div>
        )}
        <label className="form-label">Note</label>
        <textarea className="form-control" rows={3} value={notes} onChange={e => setNotes(e.target.value)} />
        {error && <div className="alert alert-danger py-2 mt-3 mb-0" style={{ fontSize: '.85rem' }}>{error}</div>}
      </form>
    </Modal>
  );
};

const Matches: React.FC = () => {
  const { user } = useAuth();
  const [matches, setMatches] = useState<Match[]>([]);
  const [openMatches, setOpenMatches] = useState<OpenMatchRequest[]>([]);
  const [levels, setLevels] = useState<PlayerLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingOpen, setLoadingOpen] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showOpenAdd, setShowOpenAdd] = useState(false);
  const [scoreTarget, setScoreTarget] = useState<Match | null>(null);
  const [editTarget, setEditTarget] = useState<Match | null>(null);
  const [savingAction, setSavingAction] = useState<number | null>(null);
  const [deletingMatchId, setDeletingMatchId] = useState<number | null>(null);
  const [deletingOpenId, setDeletingOpenId] = useState<number | null>(null);
  const [savingOpenId, setSavingOpenId] = useState<number | null>(null);
  const [filterType, setFilterType] = useState('all');
  const [filterResult, setFilterResult] = useState('all');

  const loadMatches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await matchesAPI.getAll({ mine: true });
      if (res.success && res.data) setMatches(res.data.matches || []);
      else setError(res.message || 'Nu s-au putut încărca meciurile.');
    } catch (err: any) {
      setError(err.message || 'A apărut o eroare.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOpenMatches = useCallback(async () => {
    try {
      setLoadingOpen(true);
      const res = await matchesAPI.getOpenMatches({ status: 'open' });
      if (res.success && res.data) setOpenMatches(res.data.open_matches || []);
    } catch {
      // Keep the main matches experience usable if open matches fail.
    } finally {
      setLoadingOpen(false);
    }
  }, []);

  const load = useCallback(async () => {
    await Promise.all([loadMatches(), loadOpenMatches()]);
    window.dispatchEvent(new Event('matches:pending-actions-changed'));
  }, [loadMatches, loadOpenMatches]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    metadataAPI.getAll()
      .then(res => {
        if (res.success && res.data) {
          setLevels([...res.data.player_levels].sort((a, b) => a.rank_order - b.rank_order));
        }
      })
      .catch(() => {});
  }, []);

  const stats = useMemo(() => {
    const done = matches.filter(m => m.status === 'completed');
    const wins = done.filter(m => resultFor(m, user?.id) === 'win').length;
    const losses = done.filter(m => resultFor(m, user?.id) === 'loss').length;
    const ties = done.filter(m => resultFor(m, user?.id) === 'tie').length;
    return { total: done.length, wins, losses, ties, winRate: done.length ? Math.round(wins / done.length * 100) : 0 };
  }, [matches, user?.id]);

  const pendingConfirmations = matches.filter(m =>
    m.status === 'pending_confirmation' &&
    m.confirmation_status === 'pending' &&
    m.submitted_by !== user?.id
  );

  const outgoingPending = matches.filter(m =>
    m.status === 'pending_confirmation' &&
    m.confirmation_status === 'pending' &&
    m.submitted_by === user?.id
  );

  const disputedMatches = matches.filter(m => m.confirmation_status === 'disputed');

  const filtered = matches.filter(m => {
    const typeOk = filterType === 'all' || m.match_type_slug === filterType || m.match_type === filterType;
    const r = resultFor(m, user?.id);
    const resultOk = filterResult === 'all' || r === filterResult;
    return typeOk && resultOk;
  });

  const confirm = async (matchId: number) => {
    setSavingAction(matchId);
    try {
      const res = await matchesAPI.confirmResult(matchId);
      if (res.success) load();
      else setError(res.message || 'Nu s-a putut confirma scorul.');
    } catch (err: any) {
      setError(err.message || 'A apărut o eroare.');
    } finally {
      setSavingAction(null);
    }
  };

  const dispute = async (matchId: number) => {
    setSavingAction(matchId);
    try {
      const res = await matchesAPI.disputeResult(matchId);
      if (res.success) load();
      else setError(res.message || 'Nu s-a putut contesta scorul.');
    } catch (err: any) {
      setError(err.message || 'A apărut o eroare.');
    } finally {
      setSavingAction(null);
    }
  };

  const acceptOpenMatch = async (openMatchId: number) => {
    setSavingOpenId(openMatchId);
    try {
      const res = await matchesAPI.updateOpenMatch(openMatchId, 'accept');
      if (res.success) load();
      else setError(res.message || 'Nu s-a putut accepta provocarea.');
    } catch (err: any) {
      setError(err.message || 'A apărut o eroare.');
    } finally {
      setSavingOpenId(null);
    }
  };

  const deleteOpenMatch = async (openMatchId: number) => {
    if (!window.confirm('Ștergi această provocare deschisă?')) return;
    setDeletingOpenId(openMatchId);
    try {
      const res = await matchesAPI.updateOpenMatch(openMatchId, 'cancel');
      if (res.success) load();
      else setError(res.message || 'Provocarea nu a putut fi ștearsă.');
    } catch (err: any) {
      setError(err.message || 'A apărut o eroare.');
    } finally {
      setDeletingOpenId(null);
    }
  };

  const deleteMatch = async (matchId: number) => {
    if (!window.confirm('Ștergi acest meci programat?')) return;
    setDeletingMatchId(matchId);
    try {
      const res = await matchesAPI.delete(matchId);
      if (res.success) load();
      else setError(res.message || 'Meciul nu a putut fi șters.');
    } catch (err: any) {
      setError(err.message || 'A apărut o eroare.');
    } finally {
      setDeletingMatchId(null);
    }
  };

  return (
    <div className="page">
      <div className="container page-inner">
        <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
          <div className="page-header mb-0">
            <h2>Meciuri</h2>
            <p>Adaugă rezultate, confirmă scoruri și urmărește istoricul tău</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)} style={{ borderRadius: 12, fontWeight: 800 }}>
            + Adaugă meci
          </button>
        </div>

        <div className="row g-3 mb-4">
          {([
            ['Meciuri jucate', stats.total, '#21257B'],
            ['Victorii', stats.wins, '#10b981'],
            ['Înfrângeri', stats.losses, '#ef4444'],
            ['Egaluri', stats.ties, '#64748b'],
            ['Rată victorii', `${stats.winRate}%`, '#EF8628'],
          ] as [string, string | number, string][]).map(([label, value, color]) => (
            <div key={label} className="col-6 col-lg">
              <div className="entity-card h-100">
                <div className="entity-card-body">
                  <div style={{ fontSize: '.72rem', color: 'var(--tt-text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>{label}</div>
                  <div style={{ fontSize: '1.55rem', fontWeight: 900, color }}>{value}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {pendingConfirmations.length > 0 && (
          <div className="entity-card mb-4">
            <div className="entity-card-body">
              <h5 style={{ fontWeight: 800, marginBottom: '1rem' }}>Scoruri de confirmat</h5>
              <div style={{ display: 'grid', gap: '.75rem' }}>
                {pendingConfirmations.map(m => (
                  <div key={m.id} style={{
                    border: '1px solid var(--tt-border)', borderRadius: 12, padding: '1rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap',
                  }}>
                    <div>
                      <div style={{ fontWeight: 800 }}>vs {opponentName(m, user?.id)}</div>
                      <div style={{ color: 'var(--tt-text-muted)', fontSize: '.8rem' }}>
                        {fmtDate(m.match_date)} · {fmtTime(m.match_time)} · {m.player1_score}-{m.player2_score}
                      </div>
                      <div style={{ color: 'var(--tt-text-muted)', fontSize: '.76rem' }}>{setSummary(m.sets)}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '.5rem' }}>
                      <button className="btn btn-outline-primary btn-sm" disabled={savingAction === m.id} onClick={() => setScoreTarget(m)}>Editează scor</button>
                      <button className="btn btn-outline-danger btn-sm" disabled={savingAction === m.id} onClick={() => dispute(m.id)}>Contestă</button>
                      <button className="btn btn-primary btn-sm" disabled={savingAction === m.id} onClick={() => confirm(m.id)}>Confirmă</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {(outgoingPending.length > 0 || disputedMatches.length > 0) && (
          <div className="row g-4 mb-4">
            {outgoingPending.length > 0 && (
              <div className="col-lg-6">
                <div className="entity-card h-100">
                  <div className="entity-card-body">
                    <h5 style={{ fontWeight: 800, marginBottom: '1rem' }}>În așteptarea adversarului</h5>
                    <div style={{ display: 'grid', gap: '.75rem' }}>
                      {outgoingPending.map(m => (
                        <div key={m.id} style={{ border: '1px solid var(--tt-border)', borderRadius: 12, padding: '1rem' }}>
                          <div style={{ fontWeight: 800 }}>vs {opponentName(m, user?.id)}</div>
                          <div style={{ color: 'var(--tt-text-muted)', fontSize: '.8rem' }}>
                            {m.player1_score}-{m.player2_score} · auto-confirmare {hoursUntil(m.confirmation_due_at) ?? 'în 24h'}
                          </div>
                          <div style={{ color: 'var(--tt-text-muted)', fontSize: '.76rem' }}>{setSummary(m.sets)}</div>
                          <button
                            className="btn btn-outline-primary btn-sm mt-2"
                            onClick={() => setScoreTarget(m)}
                            style={{ borderRadius: 8 }}
                          >
                            Editează scor
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {disputedMatches.length > 0 && (
              <div className="col-lg-6">
                <div className="entity-card h-100">
                  <div className="entity-card-body">
                    <h5 style={{ fontWeight: 800, marginBottom: '1rem' }}>Scoruri contestate</h5>
                    <div style={{ display: 'grid', gap: '.75rem' }}>
                      {disputedMatches.map(m => (
                        <div key={m.id} style={{ border: '1px solid #fecaca', background: '#fef2f2', borderRadius: 12, padding: '1rem' }}>
                          <div style={{ fontWeight: 800 }}>vs {opponentName(m, user?.id)}</div>
                          <div style={{ color: '#991b1b', fontSize: '.8rem' }}>
                            Scor contestat. Pentru moment nu contează în clasament.
                          </div>
                          <div style={{ color: 'var(--tt-text-muted)', fontSize: '.76rem' }}>{setSummary(m.sets)}</div>
                          <button
                            className="btn btn-outline-primary btn-sm mt-2"
                            onClick={() => setScoreTarget(m)}
                            style={{ borderRadius: 8 }}
                          >
                            Editează scor
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="entity-card mb-4">
          <div className="entity-card-body">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <div>
                <h5 style={{ fontWeight: 800, margin: 0 }}>Provocări deschise</h5>
                <div style={{ color: 'var(--tt-text-muted)', fontSize: '.82rem' }}>Găsește un adversar pentru un meci amical sau competitiv</div>
              </div>
              <button className="btn btn-outline-primary btn-sm" onClick={() => setShowOpenAdd(true)} style={{ borderRadius: 10, fontWeight: 700 }}>
                + Postează provocare
              </button>
            </div>

            {loadingOpen ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', color: 'var(--tt-text-muted)', fontSize: '.85rem' }}>
                <span className="spinner-border spinner-border-sm" style={{ color: 'var(--tt-primary)' }} />
                Se încarcă provocările…
              </div>
            ) : openMatches.length === 0 ? (
              <div className="tt-empty" style={{ padding: '1.5rem 1rem' }}>
                <div className="tt-empty-icon">🎯</div>
                <p>Nicio provocare deschisă momentan</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '.75rem' }}>
                {openMatches.map(ch => {
                  const own = ch.created_by === user?.id;
                  const allowedLevels = ch.allowed_levels || [];
                  const hasLevelRestriction = allowedLevels.length > 0;
                  const userLevelAllowed = !hasLevelRestriction || (!!user?.level_id && allowedLevels.some(level => level.id === user.level_id));
                  const startLabel = ch.is_flexible_time ? 'Orar flexibil' : fmtDateTime(ch.requested_start_datetime);
                  const creatorName = `${ch.creator_first_name || ''} ${ch.creator_last_name || ''}`.trim() || 'Jucător';
                  const creatorPath = playerPath({
                    id: ch.created_by,
                    first_name: ch.creator_first_name || '',
                    last_name: ch.creator_last_name || '',
                  });
                  return (
                    <div key={ch.id} style={{
                      border: '1px solid var(--tt-border)', borderRadius: 12,
                      padding: '1rem', display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
                    }}>
                      <div style={{ minWidth: 0, flex: '1 1 360px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', minWidth: 0 }}>
                          <Link to={creatorPath} style={{ textDecoration: 'none', flexShrink: 0 }}>
                            <AvatarBubble name={creatorName} src={ch.creator_avatar_url} size={44} />
                          </Link>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '.45rem', flexWrap: 'wrap' }}>
                              <Link
                                to={creatorPath}
                                style={{ color: 'var(--tt-text)', textDecoration: 'none', fontWeight: 900 }}
                              >
                                {creatorName}
                              </Link>
                              <LevelChip name={ch.creator_level_name} />
                              <TypeChip slug={ch.requested_match_type_slug} />
                              {own && <span className="badge text-bg-secondary">a ta</span>}
                            </div>
                            <div style={{ color: 'var(--tt-text-muted)', fontSize: '.8rem', marginTop: '.18rem' }}>
                              {startLabel || 'Orar flexibil'}
                            </div>
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '.35rem', flexWrap: 'wrap', marginTop: '.65rem' }}>
                          <span style={{ color: 'var(--tt-text-muted)', fontSize: '.76rem', fontWeight: 800 }}>Niveluri acceptate:</span>
                          {allowedLevels.length > 0
                            ? allowedLevels.map(level => <LevelChip key={level.id} name={level.display_name} muted={!!user?.level_id && level.id !== user.level_id} />)
                            : <LevelChip name={ch.requested_level_name || 'Orice nivel'} />
                          }
                        </div>
                        {ch.notes && <div style={{ color: 'var(--tt-text-2)', fontSize: '.82rem', marginTop: '.25rem' }}>{ch.notes}</div>}
                      </div>
                      {!own && (
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={savingOpenId === ch.id || !userLevelAllowed}
                          onClick={() => acceptOpenMatch(ch.id)}
                          title={!userLevelAllowed ? 'Nivelul tău nu este acceptat pentru această provocare' : undefined}
                          style={{ borderRadius: 9, fontWeight: 700 }}
                        >
                          {userLevelAllowed ? 'Acceptă' : 'Nivel neacceptat'}
                        </button>
                      )}
                      {own && (
                        <button
                          className="btn btn-outline-danger btn-sm"
                          disabled={deletingOpenId === ch.id}
                          onClick={() => deleteOpenMatch(ch.id)}
                          style={{ borderRadius: 9, fontWeight: 700 }}
                        >
                          Șterge
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="entity-card">
          <div className="entity-card-body">
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <h5 style={{ fontWeight: 800, margin: 0 }}>Istoric meciuri</h5>
              <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
                {[
                  ['all', 'Toate'], ['friendly', 'Amicale'], ['competitive', 'Competitive'], ['tournament', 'Turnee'],
                ].map(([key, label]) => (
                  <button key={key} onClick={() => setFilterType(key)} style={{
                    border: `1.5px solid ${filterType === key ? 'var(--tt-primary)' : 'var(--tt-border)'}`,
                    background: filterType === key ? 'var(--tt-primary)' : 'var(--tt-surface)',
                    color: filterType === key ? '#fff' : 'var(--tt-text-2)',
                    borderRadius: 999, padding: '.28rem .75rem', fontSize: '.8rem',
                  }}>{label}</button>
                ))}
                {[
                  ['all', 'Orice rezultat'], ['win', 'Victorii'], ['loss', 'Înfrângeri'], ['tie', 'Egaluri'], ['pending', 'În așteptare'],
                ].map(([key, label]) => (
                  <button key={key} onClick={() => setFilterResult(key)} style={{
                    border: `1.5px solid ${filterResult === key ? 'var(--tt-accent)' : 'var(--tt-border)'}`,
                    background: filterResult === key ? '#fef3e8' : 'var(--tt-surface)',
                    color: filterResult === key ? '#c2410c' : 'var(--tt-text-2)',
                    borderRadius: 999, padding: '.28rem .75rem', fontSize: '.8rem',
                  }}>{label}</button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="tt-loading">
                <span className="spinner-border spinner-border-sm" style={{ color: 'var(--tt-primary)' }} />
                <span>Se încarcă meciurile…</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="tt-empty">
                <div className="tt-empty-icon">🏓</div>
                <h5>Niciun meci găsit</h5>
                <p>Adaugă primul meci sau ajustează filtrele</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '.6rem' }}>
                {filtered.map(m => {
                  const r = resultFor(m, user?.id);
                  const slug = m.match_type_slug || m.match_type;
                  const p1Name = playerName(m, 'player1') || 'Jucător';
                  const p2Name = playerName(m, 'player2') || 'Jucător';
                  const p1Path = playerPath({
                    id: m.player1_id,
                    first_name: m.player1_first_name || '',
                    last_name: m.player1_last_name || '',
                  });
                  const p2Path = playerPath({
                    id: m.player2_id,
                    first_name: m.player2_first_name || '',
                    last_name: m.player2_last_name || '',
                  });
                  const isOwner = m.player1_id === user?.id;
                  const isParticipant = m.player1_id === user?.id || m.player2_id === user?.id;
                  const isLocked = m.status === 'completed';
                  const canEditMatch = isOwner && !isLocked;
                  const canEditScore = isParticipant && !isLocked;
                  const canDeleteMatch = isOwner && m.status === 'scheduled';
                  const cardAccent =
                    r === 'win' ? '#10b981' :
                    r === 'loss' ? '#ef4444' :
                    r === 'tie' ? '#64748b' :
                    'var(--tt-border)';
                  return (
                    <div key={m.id} className="match-card" style={{
                      position: 'relative',
                      border: '1px solid var(--tt-border)', borderRadius: 14,
                      padding: '1rem 1.1rem', background: 'var(--tt-surface)',
                      boxShadow: 'var(--sh-sm)', overflow: 'hidden',
                    }}>
                      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: cardAccent }} />

                      <div className="match-card-head" style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: '.75rem', flexWrap: 'wrap', marginBottom: '.85rem',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '.45rem', flexWrap: 'wrap' }}>
                          <ResultBadge result={r} />
                          <TypeChip slug={slug} />
                          {m.status === 'pending_confirmation' && <span className="badge text-bg-warning">confirmare</span>}
                          {m.confirmation_status === 'disputed' && <span className="badge text-bg-danger">contestat</span>}
                        </div>
                        <div style={{ color: 'var(--tt-text-muted)', fontSize: '.78rem', textAlign: 'right' }}>
                          {fmtDate(m.match_date)} · {fmtTime(m.match_time)}
                        </div>
                      </div>

                      <div className="match-card-versus" style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) minmax(140px, auto) minmax(0, 1fr)',
                        alignItems: 'center', gap: '1rem',
                      }}>
                        <MiniPlayer name={p1Name} avatar={playerAvatar(m, 'player1')} to={p1Path} />

                        <div className="match-score-box" style={{
                          textAlign: 'center', justifySelf: 'center',
                          background: 'var(--tt-surface-2)', border: '1px solid var(--tt-border)',
                          borderRadius: 14, padding: '.65rem .9rem', minWidth: 140,
                        }}>
                          <div style={{
                            fontSize: m.status === 'scheduled' ? '.8rem' : '1.65rem',
                            fontWeight: 950, lineHeight: 1,
                            color: m.status === 'scheduled' ? 'var(--tt-text-muted)' : 'var(--tt-text)',
                          }}>
                            {m.status === 'scheduled' ? 'VS' : `${m.player1_score} - ${m.player2_score}`}
                          </div>
                          <div style={{ color: 'var(--tt-text-muted)', fontSize: '.68rem', fontWeight: 800, marginTop: '.25rem', textTransform: 'uppercase' }}>
                            {m.status === 'scheduled' ? 'programat' : 'seturi'}
                          </div>
                          <SetStrip sets={m.sets} player1Name={p1Name} player2Name={p2Name} />
                        </div>

                        <MiniPlayer name={p2Name} avatar={playerAvatar(m, 'player2')} align="right" to={p2Path} />
                      </div>

                      <div className="match-card-foot" style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: '.75rem', flexWrap: 'wrap', marginTop: '.85rem',
                      }}>
                        <div style={{ minHeight: 20 }}>
                        {m.status === 'pending_confirmation' && m.submitted_by === user?.id && m.confirmation_status === 'pending' && (
                          <div style={{ color: 'var(--tt-text-muted)', fontSize: '.78rem' }}>
                            Se confirmă automat {hoursUntil(m.confirmation_due_at) ?? 'în 24h'}
                          </div>
                        )}
                        {m.confirmation_status === 'disputed' && (
                          <div style={{ color: '#991b1b', fontSize: '.78rem', fontWeight: 700 }}>
                            Scor contestat
                          </div>
                        )}
                        </div>

                        {(canEditMatch || canEditScore || canDeleteMatch) && (
                          <div className="match-actions" style={{ display: 'flex', gap: '.35rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            {canEditMatch && (
                              <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => setEditTarget(m)}
                                style={{ borderRadius: 8, whiteSpace: 'nowrap' }}
                              >
                                Editează
                              </button>
                            )}
                            {canEditScore && (
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => setScoreTarget(m)}
                                style={{ borderRadius: 8, whiteSpace: 'nowrap' }}
                              >
                                Scor
                              </button>
                            )}
                            {canDeleteMatch && (
                              <button
                                className="btn btn-outline-danger btn-sm"
                                disabled={deletingMatchId === m.id}
                                onClick={() => deleteMatch(m.id)}
                                style={{ borderRadius: 8, whiteSpace: 'nowrap' }}
                              >
                                Șterge
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {showAdd && user && <AddMatchModal currentUser={user} onClose={() => setShowAdd(false)} onCreated={load} />}
      {editTarget && user && (
        <AddMatchModal
          currentUser={user}
          match={editTarget}
          onClose={() => setEditTarget(null)}
          onCreated={load}
        />
      )}
      {showOpenAdd && user && (
        <OpenMatchModal
          currentUser={user}
          levels={levels}
          onClose={() => setShowOpenAdd(false)}
          onCreated={load}
        />
      )}
      {scoreTarget && user && (
        <ScoreMatchModal
          match={scoreTarget}
          currentUser={user}
          onClose={() => setScoreTarget(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};

export default Matches;
